import './StatusBlock.css';
import { relativeTime, deadlineTone, fmtShortDate } from './NewRail';

// ============================================================
// STATUS BLOCK — the sweep-owned synopsis on a card. Three lines on
// a light surface: where we stand, whose move (pill + sentence),
// next check (date chip) · updated <when> · auto.
//
// Below CONFIDENCE_MIN, or with no synopsis at all, one quiet line.
// Pure presentation: no fetch, no MSAL. The optional court hint is a
// button the parent wires to setCourt.
//
// The left bar is the card's PROJECT color (Phase A): the block inherits
// --proj-color from the Card that wraps it (StatusBlock.css), so it needs
// no lane lookup of its own. Outside a Card it falls back to neutral.
// ============================================================

export const CONFIDENCE_MIN = 0.5;

export function hasStatus(item) {
  const s = item?.synopsis;
  return !!(s && s.standing && typeof s.confidence === 'number' && s.confidence >= CONFIDENCE_MIN);
}

// The pill from the synopsis court. Court on the card decides "Your move"
// vs "Their move" when they agree; the hint (parent) covers the rest.
function movePill(court) {
  if (court === 'Mine') return { cls: 'StatusBlock-pill--mine', text: 'Your move' };
  if (court === 'Theirs') return { cls: 'StatusBlock-pill--theirs', text: 'Their move' };
  if (court === 'Holding') return { cls: 'StatusBlock-pill--parked', text: 'Parked' };
  return null;
}

export default function StatusBlock({ item, today = new Date(), onSuggestCourt }) {
  const s = item?.synopsis;
  if (!hasStatus(item)) {
    // A card with no thread can never get a synopsis from a sweep; say so.
    const noThread = !(item?.sources && item.sources.length > 0);
    return (
      <div className="StatusBlock StatusBlock--empty" data-testid={`status-${item?.id}`}>
        <span className="StatusBlock-lane" aria-hidden="true" />
        <div className="StatusBlock-quiet">{noThread ? 'No thread yet — status starts with the first email.' : 'No status yet — runs on the next sweep'}</div>
      </div>
    );
  }
  const pill = movePill(s.court);
  const tone = s.nextCheck ? deadlineTone(s.nextCheck, today) : null;
  const disagree = s.court && item.court && s.court !== item.court && (s.court === 'Mine' || s.court === 'Theirs');
  return (
    <div className="StatusBlock" data-testid={`status-${item.id}`}>
      <span className="StatusBlock-lane" aria-hidden="true" />
      <div className="StatusBlock-body">
        <div className="StatusBlock-standing">{s.standing}</div>
        <div className="StatusBlock-move">
          {pill && <span className={`StatusBlock-pill ${pill.cls}`}>{pill.text}</span>}
          {disagree && onSuggestCourt && (
            <button type="button" className="StatusBlock-hint" title={`The sweep reads this as ${s.court}. Click to set Court.`}
              onClick={() => onSuggestCourt(item, s.court)}>suggests: {s.court}</button>
          )}
          <span className="StatusBlock-move-text">{s.move || '—'}</span>
        </div>
        <div className="StatusBlock-next">
          {s.nextCheck ? (
            <span className={`StatusBlock-chip${tone === 'past' ? ' StatusBlock-chip--past' : ''}`}>Next check {fmtShortDate(s.nextCheck)}</span>
          ) : (
            <span className="StatusBlock-chip StatusBlock-chip--none">No next check</span>
          )}
          {s.at && <span className="StatusBlock-updated">· updated {relativeTime(s.at, today)}</span>}
          <span className="StatusBlock-auto" title="written by the sweep from the thread; your Notes are separate">auto</span>
        </div>
      </div>
    </div>
  );
}
